import {AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {Category, Course, Tag, User} from '../../shared/models';
import {forkJoin, Subject} from 'rxjs';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {CourseService} from '../../shared/services';
import {takeUntil} from 'rxjs/operators';
import {CourseUtil, SeoUtil, WindowUtil} from '../../shared/utils';
import {CmsService} from '../../shared/services/cms.service';
import {AuthService} from '../../shared/modules/auth/auth.service';
import {TranslateService} from '@ngx-translate/core';
import {environment} from '../../../environments/environment';

declare var MainJs: any;

@Component({
  selector: 'app-courses',
  templateUrl: './courses.component.html'
})
export class CoursesComponent implements OnInit, OnDestroy, AfterViewInit {
  readonly pageSize: number = 9;

  user: User;

  tags: Array<Tag> = [];
  courseCategories: Array<Category> = [];
  withPointsCount: number;
  withoutPointsCount: number;

  courses: Array<Course> = [];
  totalPages: number = 0;
  selectedPage: number = 1;
  selectedPoints: Array<string> = [];
  selectedCategories: Array<string> = [];
  selectedTypes: Array<string> = [];

  firstLoaded: boolean = false;

  loading: boolean;

  private destroy$ = new Subject();

  constructor(public courseUtil: CourseUtil,
              private windowUtil: WindowUtil,
              private route: ActivatedRoute,
              private router: Router,
              private cd: ChangeDetectorRef,
              private cmsService: CmsService,
              private courseService: CourseService,
              private authService: AuthService,
              private translateService: TranslateService,
              private seoUtil: SeoUtil) {
  }

  ngOnInit(): void {
    this.mapQueryParams(this.route.snapshot.queryParams);
    this.seoUtil.updateTitle(this.translateService.instant('Stručne edukacije iz medicine'));
    this.seoUtil.updateMetaTags([
      {property: 'og:title', content: this.translateService.instant('Stručne edukacije iz medicine')},
      {property: 'og:url', content: environment.sites.root + 'courses'}
    ]);
    this.handleRouteChange();
    this.handleUserChange();
    this.fetchFilterData();
  }

  ngOnDestroy(): void {
    this.seoUtil.setDefaultMetaTags();
    this.seoUtil.setDefaultTitle();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    this.cd.detectChanges();
    MainJs.collapsible();
  }

  toggleFilterArray(array: Array<string>, id: number): void {
    let pointsIndex = array.findIndex(item => +item === id);
    if (pointsIndex > -1) {
      array.splice(pointsIndex, 1);
    } else {
      array.push(id.toString());
    }
    this.updateRoute();
  }

  isFilterChecked(array: Array<string>, id: number): boolean {
    return array.indexOf(id.toString()) > -1;
  }

  private handleUserChange() {
    if (this.authService.isLoggedIn()) {
      this.authService.getAuthUser().subscribe(user => {
        this.user = user;
      });
    } else {
      this.user = null;
    }
    this.authService.userChanged()
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.user = user;
      });
  }

  private updateRoute(): void {
    let queryParams = {
      page: undefined,
      points: undefined,
      types: undefined,
      categories: undefined,
    };
    if (this.selectedPoints && this.selectedPoints.length > 0) {
      queryParams['points'] = this.selectedPoints.join(',');
    }
    if (this.selectedTypes && this.selectedTypes.length > 0) {
      queryParams['types'] = this.selectedTypes.join(',');
    }
    if (this.selectedCategories && this.selectedCategories.length > 0) {
      queryParams['categories'] = this.selectedCategories.join(',');
    }

    this.router.navigate([], {
      relativeTo: this.route, queryParamsHandling: 'merge',
      queryParams: queryParams
    }).finally();
  }

  private fetchFilterData(): void {
    forkJoin([
      this.cmsService.getTags('COURSE'),
      this.cmsService.getCategories('COURSES'),
      this.courseService.getCourses(1, 0, {minPoints: '0.0001'}),
      this.courseService.getCourses(1, 0, {minPoints: '0', maxPoints: '0'})
    ]).subscribe(([tags, categories, withPoints, withoutPoints]) => {
      this.tags = tags.sort((a, b) => b.itemCount - a.itemCount);
      this.courseCategories = categories.sort((a, b) => b.itemCount - a.itemCount);
      this.withPointsCount = withPoints.total_count;
      this.withoutPointsCount = withoutPoints.total_count;
    });
  }

  private loadPage(scrollToCourses: boolean = false): void {
    if (this.selectedPage < 0) {
      return;
    }
    this.fetchCourses(scrollToCourses);
  }

  private fetchCourses(scrollToCourses: boolean = false): void {
    let minMaxPoints = this.courseUtil.minMaxPoints(this.selectedPoints);
    this.loading = true;
    this.courseService.getCourses(this.selectedPage, this.pageSize, {
      minPoints: minMaxPoints[0],
      maxPoints: minMaxPoints[1],
      typeIds: this.selectedTypes.join(','),
      categoryIds: this.selectedCategories.join(','),
      active: true
    }).subscribe(resp => {
      this.courses = resp.data;
      this.totalPages = resp.total_pages;
      this.loading = false;
      this.firstLoaded = true;
      this.cd.detectChanges();
      MainJs.progress();
      this.windowUtil.enableScrolling();
      if (scrollToCourses) {
        this.windowUtil.scrollTo('hero-container', 55);
      }
    }, () => {
      this.windowUtil.enableScrolling();
    });
  }

  private handleRouteChange(): void {
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(query => {
        this.windowUtil.disableScrolling();
        this.mapQueryParams(query);
        this.loadPage(this.firstLoaded || Object.keys(query).length > 0);
      });
  }

  private mapQueryParams(paramMap: Params): void {
    this.selectedPage = paramMap['page'] ? +paramMap['page'] : 1;
    this.selectedPoints = paramMap['points'] ? paramMap['points'].split(',') : [];
    this.selectedTypes = paramMap['types'] ? paramMap['types'].split(',') : [];
    this.selectedCategories = paramMap['categories'] ? paramMap['categories'].split(',') : [];
  }

}
